import React, { useCallback, useState } from 'react';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useAuth } from '../../contexts/AuthContext';
import { showGuestAlert } from '../../utils/guestReadOnlyAlert';
import { ensureChat } from '../../services/chatService';
import {
  getBlockedUserIdsSet,
  getPlaydateMatches,
  isProfileComplete,
} from '../../services/userService';
import type { MainStackParamList } from '../../navigation/types';
import type { UserProfile } from '../../types/models';
import { getBabyAgeInMonths } from '../../utils/babyAge';
import { impactLight } from '../../utils/haptics';

const PLAYDATE_INITIAL_MESSAGE =
  'Merhaba! Bebeklerimiz aynı ayda ve aynı ilçedeyiz, tanışıp parka çıkmak ister misin?';

function formatPlaydateBabyLabel(months: number): string {
  return `${months} Aylık`;
}

function formatPlaydateLocation(u: UserProfile): string {
  const parts = [u.district?.trim(), u.city?.trim()].filter(Boolean);
  return parts.join(', ');
}

export function PlaydatesScreen() {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<MainStackParamList>>();
  const { user, profile, logout } = useAuth();
  const [matches, setMatches] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openingId, setOpeningId] = useState<string | null>(null);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!user?.uid || user.isAnonymous || !profile || !isProfileComplete(profile)) {
        setMatches([]);
        setLoading(false);
        setRefreshing(false);
        return;
      }
      if (!opts?.silent) {
        setLoading(true);
      }
      try {
        const list = await getPlaydateMatches(profile);
        const blocked = getBlockedUserIdsSet(profile);
        setMatches(list.filter(u => !blocked.has(u.id)));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [user?.uid, user?.isAnonymous, profile],
  );

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const onMessage = useCallback(
    async (peer: UserProfile) => {
      void impactLight();
      if (user?.isAnonymous === true) {
        showGuestAlert(logout);
        return;
      }
      if (!user?.uid) {
        return;
      }
      setOpeningId(peer.id);
      try {
        const peerName = peer.displayName?.trim() || 'Annedaş';
        const { chatId } = await ensureChat(user.uid, peer.id);
        navigation.navigate('Chat', {
          chatId,
          otherUserId: peer.id,
          peerName,
          hideIcebreakers: true,
          initialMessage: PLAYDATE_INITIAL_MESSAGE,
        });
      } finally {
        setOpeningId(null);
      }
    },
    [logout, navigation, user?.isAnonymous, user?.uid],
  );

  const profileOk = Boolean(profile && isProfileComplete(profile));
  const locationOk = Boolean(profile?.city?.trim() && profile?.district?.trim());

  if (loading && matches.length === 0) {
    return (
      <View
 className="flex-1 items-center justify-center bg-white dark:bg-black"
        style={{ paddingTop: insets.top }}>
        <ActivityIndicator size="large" color="#18181b" />
 <Text className="mt-3 text-sm text-gray-500 dark:text-zinc-400">Eşleşmeler aranıyor…</Text>
      </View>
    );
  }

  return (
 <View className="flex-1 bg-white dark:bg-black">
      <FlatList
        data={matches}
        keyExtractor={item => item.id}
        contentContainerStyle={{
          paddingHorizontal: 20,
          paddingTop: 12,
          paddingBottom: 100 + insets.bottom,
        }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => {
              setRefreshing(true);
              void load({ silent: true });
            }}
            tintColor="#18181b"
          />
        }
        ListHeaderComponent={
 <View className="mb-5">
 <Text className="text-base leading-6 text-gray-600 dark:text-zinc-400">
              İlçende ve bebeğinle yakın yaşta olan anneleri bul; park buluşması için mesaj at.
            </Text>
            {!user?.uid || user.isAnonymous ? (
 <View className="mt-4 rounded-2xl border border-zinc-200 bg-amber-50/90 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900/80">
 <Text className="text-sm text-zinc-900 dark:text-zinc-100">
                  Bu özellik için kayıtlı hesapla giriş yapman gerekir.
                </Text>
              </View>
            ) : !profileOk ? (
 <View className="mt-4 rounded-2xl border border-zinc-200 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
 <Text className="text-sm text-gray-600 dark:text-zinc-400">
                  Profilini (il, ilçe, bebek doğum tarihi) tamamladığında eşleşmeler listelenir.
                </Text>
              </View>
            ) : !locationOk ? (
 <View className="mt-4 rounded-2xl border border-zinc-200 bg-white px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900">
 <Text className="text-sm text-gray-600 dark:text-zinc-400">
                  İl ve ilçe bilgisi olmadan yakın eşleşme gösterilemez.
                </Text>
              </View>
            ) : null}
          </View>
        }
        ListEmptyComponent={
          profileOk && locationOk && !user?.isAnonymous ? (
 <View className="rounded-2xl border border-zinc-200 bg-zinc-50 dark:bg-zinc-900/90 px-5 py-8 dark:border-zinc-800 dark:bg-zinc-900/80">
 <Text className="text-center text-base font-semibold text-gray-800 dark:text-zinc-100">
                Henüz eşleşme yok
              </Text>
 <Text className="mt-2 text-center text-sm leading-5 text-gray-600 dark:text-zinc-400">
                Aynı ilçede ve ±3 ay yaş aralığında başka Annedaş olunca burada görünecek.
              </Text>
            </View>
          ) : null
        }
        renderItem={({ item }) => {
          const months = getBabyAgeInMonths(item.babyBirthDate);
          return (
            <View className="mb-4 overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
 <View className="border-b border-zinc-200/80 bg-zinc-50 dark:bg-zinc-900 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/80">
                <Text
 className="text-lg font-bold text-gray-900 dark:text-zinc-100"
                  numberOfLines={2}>
                  {item.displayName}
                </Text>
 <Text className="mt-1 text-sm font-semibold text-zinc-700 dark:text-zinc-300">
                  Bebek {formatPlaydateBabyLabel(months)}
                </Text>
 <Text className="mt-1.5 text-xs text-gray-600 dark:text-zinc-400">
                  {formatPlaydateLocation(item)}
                </Text>
              </View>
 <View className="px-4 py-3">
                <TouchableOpacity
                  disabled={openingId === item.id}
                  activeOpacity={0.88}
                  onPress={() => void onMessage(item)}
                  className="self-start rounded-lg border border-zinc-900 bg-zinc-900 px-3 py-2 dark:border-zinc-50 dark:bg-zinc-50">
                  {openingId === item.id ? (
                    <ActivityIndicator color="#fff" size="small" />
                  ) : (
                    <Text className="text-center text-xs font-semibold text-white dark:text-zinc-950">
                      Mesaj Gönder
                    </Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          );
        }}
      />
    </View>
  );
}
